import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button, CircularProgress, TableCell, TableRow } from "@material-ui/core";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";
import {
  DataTable,
  FilterSelect,
  PageHeader,
  StatusChip,
  errorMessage,
  fmtDateTime,
  gbp,
  humanize,
  listOf,
  useCloStyles,
} from "./shared";

const STATUSES = ["pending", "approved", "rejected"];

const COLUMNS = [
  { label: "Requested" },
  { label: "User" },
  { label: "Direction" },
  { label: "Amount", numeric: true },
  { label: "Reason" },
  { label: "Status" },
  { label: "Requested by" },
  { label: "Decided by" },
  { label: "Actions" },
];

const Adjustments = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [status, setStatus] = useState("pending");
  const [busyId, setBusyId] = useState<number | null>(null);

  const fetchRows = async (forStatus = status) => {
    try {
      setLoading(true);
      const res: any = await axios.get(ENDPOINTS.CLO_WALLET_ADJUSTMENTS, getConfigGet(forStatus ? { status: forStatus } : {}));
      setRows(listOf(res.data));
    } catch (err) {
      notify(errorMessage(err, "Failed to load adjustments"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (a: any, action: "approve" | "reject") => {
    const verb = action === "approve" ? "Approve" : "Reject";
    const effect =
      action === "approve"
        ? `${humanize(a.direction)} ${gbp(a.amount_minor)} ${a.direction === "credit" ? "to" : "from"} user ${a.user_id}'s wallet.`
        : "Nothing changes in the wallet.";
    if (!window.confirm(`${verb} this adjustment?\n\n${a.reason}\n\n${effect}`)) return;
    try {
      setBusyId(a.id);
      await axios.patch(`${ENDPOINTS.CLO_WALLET_ADJUSTMENTS}/${a.id}`, { action }, { headers: getConfigPatch() });
      notify(action === "approve" ? "Adjustment applied to the wallet" : "Adjustment rejected", "info");
      fetchRows();
    } catch (err) {
      notify(errorMessage(err, `Failed to ${action} adjustment`), "warning");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={classes.main}>
      <PageHeader
        title="Wallet adjustments"
        subtitle="Manual credits and debits, e.g. reversing a forfeiture after a complaint. A super admin other than the requester approves each one."
      />

      <div className={classes.filterContainer}>
        <FilterSelect
          label="Status"
          value={status}
          onChange={(next) => {
            setStatus(next);
            fetchRows(next);
          }}
          options={STATUSES.map((s) => ({ value: s, label: humanize(s) }))}
        />
      </div>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <DataTable
          columns={COLUMNS}
          isEmpty={rows.length === 0}
          empty={status === "pending" ? "Nothing waiting for approval. Request an adjustment from a customer's wallet." : "No adjustments."}
        >
          {rows.map((a) => (
            <TableRow key={a.id} hover>
              <TableCell>{fmtDateTime(a.createdAt)}</TableCell>
              <TableCell><Link to={`/clo/wallets/${a.user_id}`}>{a.user_id}</Link></TableCell>
              <TableCell>{humanize(a.direction)}</TableCell>
              <TableCell align="right"><strong>{gbp(a.amount_minor)}</strong></TableCell>
              <TableCell>{a.reason}</TableCell>
              <TableCell><StatusChip status={a.status} /></TableCell>
              <TableCell>{a.requested_by}</TableCell>
              <TableCell>{a.decided_by || "—"}</TableCell>
              <TableCell>
                {a.status === "pending" ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button size="small" variant="contained" color="primary" disableElevation disabled={busyId === a.id} onClick={() => decide(a, "approve")}>
                      Approve
                    </Button>
                    <Button size="small" variant="outlined" disabled={busyId === a.id} onClick={() => decide(a, "reject")}>
                      Reject
                    </Button>
                  </div>
                ) : (
                  "—"
                )}
              </TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}

      <Notification />
    </div>
  );
};

export default Adjustments;
